// src/pages/app/NewExpenseClaimPage.jsx — Create expense claim with line items
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { expenseClaimsApi, travelRequestsApi } from '@/lib/api';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Trash2, Loader2, AlertTriangle, CheckCircle2, Send } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import FileUpload from '@/components/ui/FileUpload';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

const CATEGORIES = [
  { value: 'meals', label: '🍽️ Meals', hint: 'Policy cap: ₹1,500 / day' },
  { value: 'local_transit', label: '🚕 Local Transit', hint: 'Policy cap: ₹2,500 / trip' },
  { value: 'accommodation', label: '🏨 Accommodation', hint: 'Policy cap: ₹5,000 / night' },
  { value: 'flight', label: '✈️ Flight', hint: 'Economy class only' },
  { value: 'train', label: '🚆 Train', hint: 'AC 2-Tier / 3-Tier cap' },
  { value: 'fuel', label: '⛽ Fuel', hint: 'Reimbursement @ ₹12 / km' },
  { value: 'communication', label: '📱 Communication', hint: 'Policy cap: ₹1,000 / month' },
  { value: 'misc', label: '📎 Miscellaneous', hint: 'Requires receipt > ₹500' },
];

const POLICY_LIMITS = {
  meals: 1500,
  local_transit: 2500,
  accommodation: 5000,
  communication: 1000,
};

const inputCls = "w-full px-3 py-2.5 rounded-lg border border-slate-300 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white";
const labelCls = "block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wide";

const emptyItem = () => ({
  id: Math.random(),
  category: 'meals',
  amount: '',
  currency: 'INR',
  expense_date: new Date().toISOString().split('T')[0],
  vendor_name: '',
  description: '',
  receipt_url: '',
});

export default function NewExpenseClaimPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [policyViolations, setPolicyViolations] = useState([]);
  const [checkingPolicy, setCheckingPolicy] = useState(false);
  const [trips, setTrips] = useState([]);
  const [form, setForm] = useState({
    travel_request_id: '',
    title: '',
    currency: 'INR',
  });
  const [items, setItems] = useState([emptyItem()]);

  useEffect(() => {
    // Load completed travel requests to link claim
    travelRequestsApi.list({ status: 'booked' }).then(({ data }) => setTrips(data)).catch(() => {});
  }, []);

  const addItem = () => setItems(i => [...i, emptyItem()]);
  const removeItem = (id) => setItems(i => i.filter(x => x.id !== id));
  const updateItem = (id, field, value) => setItems(i => i.map(x => x.id === id ? { ...x, [field]: value } : x));

  const totalAmount = items.reduce((sum, i) => sum + (parseFloat(i.amount) || 0), 0);

  const checkPolicy = async () => {
    if (!items.some(i => i.amount)) return;
    setCheckingPolicy(true);
    try {
      const { data } = await expenseClaimsApi.checkPolicy(
        items.filter(i => i.amount).map(i => ({
          category: i.category,
          amount: parseFloat(i.amount),
          expense_date: i.expense_date,
        }))
      );
      setPolicyViolations(data.violations || []);
      if (!data.violations?.length) toast.success('All items are within policy limits ✓');
      else toast.warning(`${data.violations.length} policy violation(s) detected`);
    } catch { toast.error('Could not check policy'); }
    finally { setCheckingPolicy(false); }
  };

  const [createdClaimId, setCreatedClaimId] = useState(null);
  const [showSubmitClaimModal, setShowSubmitClaimModal] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validItems = items.filter(i => i.amount && parseFloat(i.amount) > 0);
    if (!validItems.length) { toast.error('Add at least one expense item with an amount'); return; }

    setLoading(true);
    try {
      const payload = {
        ...form,
        travel_request_id: form.travel_request_id || null,
        items: validItems.map(i => ({
          category: i.category,
          amount: parseFloat(i.amount),
          currency: i.currency,
          expense_date: i.expense_date,
          vendor_name: i.vendor_name,
          description: i.description,
          receipt_url: i.receipt_url,
        })),
      };
      const { data } = await expenseClaimsApi.create(payload);
      toast.success('Expense claim created!');
      setCreatedClaimId(data.id);
      setShowSubmitClaimModal(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create expense claim');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSubmitClaim = async () => {
    if (!createdClaimId) return;
    try {
      await expenseClaimsApi.submit(createdClaimId);
      toast.success('Submitted for approval!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submit failed');
    } finally {
      setShowSubmitClaimModal(false);
      navigate('/app/expense-claims');
    }
  };

  const handleSkipSubmitClaim = () => {
    setShowSubmitClaimModal(false);
    navigate('/app/expense-claims');
  };

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center gap-3">
        <Link to="/app/expense-claims" className="p-2 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold text-slate-900">New Expense Claim</h1>
          <p className="text-slate-500 text-sm">Submit post-trip expenses for reimbursement</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Claim Details */}
        <Card>
          <CardHeader><CardTitle className="text-sm sm:text-base">Claim Details</CardTitle></CardHeader>
          <CardContent className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <label className={labelCls}>Claim Title *</label>
              <input required value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                placeholder="E.g. Business trip to Delhi - October 2025" className={inputCls} />
            </div>
            <div>
              <label className={labelCls}>Link to Travel Request (optional)</label>
              <Select
                value={form.travel_request_id || 'none'}
                onValueChange={(val) => setForm(f => ({ ...f, travel_request_id: val === 'none' ? '' : val }))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="— Not linked to a trip —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— Not linked to a trip —</SelectItem>
                  {trips.map(tr => (
                    <SelectItem key={tr.id} value={tr.id}>
                      {tr.destination} ({new Date(tr.start_date).toLocaleDateString('en-IN')})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>Currency</label>
              <Select
                value={form.currency}
                onValueChange={(val) => setForm(f => ({ ...f, currency: val }))}
              >
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="INR">₹ INR</SelectItem>
                  <SelectItem value="USD">$ USD</SelectItem>
                  <SelectItem value="EUR">€ EUR</SelectItem>
                  <SelectItem value="GBP">£ GBP</SelectItem>
                  <SelectItem value="AED">AED</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Expense Items */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm sm:text-base">Expense Items</CardTitle>
              <div className="flex items-center gap-2">
                <button type="button" onClick={checkPolicy} disabled={checkingPolicy}
                  className="text-xs px-3 py-1.5 rounded-lg border border-blue-300 text-blue-700 hover:bg-blue-50 disabled:opacity-50 flex items-center gap-1.5">
                  {checkingPolicy ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                  Check Policy
                </button>
                <button type="button" onClick={addItem}
                  className="text-xs px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 flex items-center gap-1.5">
                  <Plus className="w-3.5 h-3.5" /> Add Item
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Policy violations */}
            {policyViolations.length > 0 && (
              <div className="rounded-lg bg-amber-50 border border-amber-200 p-4">
                <div className="flex items-center gap-2 mb-2">
                  <AlertTriangle className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-semibold text-amber-800">Policy Violations Detected</span>
                </div>
                {policyViolations.map((v, i) => (
                  <p key={i} className="text-xs text-amber-700">
                    • {v.category} on {v.item_date}: ₹{v.amount} exceeds limit of ₹{v.limit}
                    <span className="ml-2 text-xs font-medium uppercase">[{v.severity}]</span>
                  </p>
                ))}
              </div>
            )}

            {items.map((item, idx) => {
              const isViolated = policyViolations.some(v => v.category === item.category && v.item_date === item.expense_date);
              return (
                <div key={item.id} className={cn(
                  "p-3 sm:p-4 rounded-xl border transition-colors",
                  isViolated ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"
                )}>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Item {idx + 1}</span>
                    <div className="flex items-center gap-2">
                      {isViolated && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                      {items.length > 1 && (
                        <button type="button" onClick={() => removeItem(item.id)}
                          className="p-1 text-slate-400 hover:text-red-500 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <label className={labelCls}>Category</label>
                      <Select
                        value={item.category}
                        onValueChange={(val) => updateItem(item.id, 'category', val)}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIES.map(c => (
                            <SelectItem key={c.value} value={c.value}>
                              {c.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-[11px] text-slate-500 mt-1 font-medium">
                        {CATEGORIES.find(c => c.value === item.category)?.hint}
                      </p>
                    </div>
                    <div>
                      <label className={labelCls}>Amount *</label>
                      <input required type="number" min="0.01" step="0.01" value={item.amount}
                        onChange={e => updateItem(item.id, 'amount', e.target.value)} placeholder="0.00" className={inputCls} />
                      {POLICY_LIMITS[item.category] && item.amount ? (
                        parseFloat(item.amount) > POLICY_LIMITS[item.category] ? (
                          <span className="text-[11px] font-semibold text-amber-600 mt-1 block">
                            ⚠️ Exceeds cap (₹{POLICY_LIMITS[item.category]})
                          </span>
                        ) : (
                          <span className="text-[11px] font-semibold text-emerald-600 mt-1 block">
                            ✓ Within policy limit
                          </span>
                        )
                      ) : null}
                    </div>
                    <div>
                      <label className={labelCls}>Date *</label>
                      <input required type="date" value={item.expense_date}
                        onChange={e => updateItem(item.id, 'expense_date', e.target.value)} className={inputCls} />
                    </div>
                    <div>
                      <label className={labelCls}>Vendor / Merchant</label>
                      <input value={item.vendor_name} onChange={e => updateItem(item.id, 'vendor_name', e.target.value)}
                        placeholder="E.g. Taj Hotel" className={inputCls} />
                    </div>
                    <div className="sm:col-span-2">
                      <label className={labelCls}>Description</label>
                      <input value={item.description} onChange={e => updateItem(item.id, 'description', e.target.value)}
                        placeholder="Brief description of the expense" className={inputCls} />
                    </div>
                    <div className="sm:col-span-3">
                      <FileUpload
                        label="Upload Bill / Receipt Attachment"
                        value={item.receipt_url}
                        onChange={url => updateItem(item.id, 'receipt_url', url)}
                        hint="Upload receipt scan, photo, or PDF bill (PNG, JPG, WEBP, PDF up to 10MB)"
                      />
                    </div>
                  </div>
                </div>
              );
            })}

            {/* Total */}
            <div className="flex justify-end pt-2 border-t border-slate-200">
              <div className="text-right">
                <p className="text-xs text-slate-400 uppercase tracking-wide">Total Claimed</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-900">₹{totalAmount.toLocaleString('en-IN')}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-3 justify-end">
          <Link to="/app/expense-claims"
            className="px-6 py-2.5 rounded-lg border border-slate-300 text-sm font-semibold text-slate-700 hover:bg-slate-50 transition-colors">
            Cancel
          </Link>
          <button type="submit" disabled={loading}
            className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-semibold hover:bg-blue-700 transition-colors disabled:opacity-60">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin" />Creating...</> : 'Create Claim'}
          </button>
        </div>
      </form>

      {/* Submit Confirmation Dialog */}
      <AlertDialog open={showSubmitClaimModal} onOpenChange={setShowSubmitClaimModal}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <div className="w-12 h-12 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center mb-1 mx-auto sm:mx-0">
              <Send className="w-6 h-6" />
            </div>
            <AlertDialogTitle className="text-lg sm:text-xl font-bold text-slate-900">
              Expense Claim Created!
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-slate-600">
              Your expense claim has been created. Would you like to submit it for manager approval right now?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4 gap-2 sm:gap-0">
            <AlertDialogCancel
              onClick={handleSkipSubmitClaim}
              className="border-slate-300 text-slate-700 hover:bg-slate-100"
            >
              Keep as Draft
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmSubmitClaim}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium"
            >
              Submit for Approval
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
